<template>
    <div class="flex flex-wrap items-end gap-3">
        <div
            v-for="field in fields"
            :key="field.key"
            class="flex min-w-[10rem] flex-col"
        >
            <label
                class="text-xs font-medium uppercase tracking-wide text-gray-400"
                :for="`activity-log-${field.key}`"
            >
                {{ field.label }}
            </label>
            <select
                :id="`activity-log-${field.key}`"
                :value="filters[field.key] ?? ''"
                class="mt-1 rounded-lg border-0 px-3 py-1.5 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
                @change="applyFilter(field.key, $event)"
            >
                <option value="">{{ t("vue.kiosk_activity_logs_all") }}</option>
                <option
                    v-for="value in field.options"
                    :key="value"
                    :value="value"
                >
                    {{ value }}
                </option>
            </select>
        </div>

        <button
            v-if="hasActiveFilter"
            type="button"
            class="rounded-lg bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            @click="clearFilters"
        >
            {{ t("vue.kiosk_activity_logs_clear_filters") }}
        </button>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useTranslations } from "@/composables/useTranslations";
import {
    EMPTY_ACTIVITY_LOG_FILTERS,
    type ActivityLogFilterKey,
    type IActivityLogFilterOptions,
    type IActivityLogFilters,
} from "@/types/ActivityLog/IActivityLogFilters";

interface IFilterField {
    key: ActivityLogFilterKey;
    label: string;
    options: string[];
}

const { t } = useTranslations();

const props = defineProps<{
    filters: IActivityLogFilters;
    filterOptions: IActivityLogFilterOptions;
}>();

const emit = defineEmits<{
    (event: "change", filters: IActivityLogFilters): void;
}>();

const fields = computed((): IFilterField[] => [
    {
        key: "entity_type",
        label: t("vue.kiosk_da_al_entity_type"),
        options: props.filterOptions.entity_types,
    },
    {
        key: "event_code",
        label: t("vue.kiosk_da_al_event_code"),
        options: props.filterOptions.event_codes,
    },
    {
        key: "source",
        label: t("vue.kiosk_da_al_source"),
        options: props.filterOptions.sources,
    },
    {
        key: "operation_type",
        label: t("vue.kiosk_da_al_operation_type"),
        options: props.filterOptions.operation_types,
    },
]);

const hasActiveFilter = computed((): boolean =>
    fields.value.some((field) => props.filters[field.key] !== null),
);

const applyFilter = (key: ActivityLogFilterKey, event: Event): void => {
    const value = (event.target as HTMLSelectElement).value;

    emit("change", { ...props.filters, [key]: value === "" ? null : value });
};

const clearFilters = (): void => {
    emit("change", { ...EMPTY_ACTIVITY_LOG_FILTERS });
};
</script>
